import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Filter,
  X,
  RotateCcw,
  Check,
  Calendar,
  Users,
  FolderOpen,
  FileText,
  PoundSterling,
  Circle,
  Zap,
  Sparkles,
  History as HistoryIcon,
  Save,
} from 'lucide-react';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { TimePeriodFilters } from './filters/TimePeriodFilters';
import { PeopleOrganizationFilters } from './filters/PeopleOrganizationFilters';
import { ProjectsFilters } from './filters/ProjectsFilters';
import { QuickTemplates } from './filters/QuickTemplates';
import type { FilterTemplate } from './filters/QuickTemplates';
import { FilterPresets } from './filters/FilterPresets';
import type { FilterPreset } from './filters/FilterPresets';
import { FilterHistory } from './filters/FilterHistory';
import type { FilterHistoryEntry } from './filters/FilterHistory';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import * as filterPresetsApi from '@/services/api/filterPresetsApi';

interface AdvancedFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TimesheetReportFilters;
  onFiltersChange: (filters: TimesheetReportFilters) => void;
  availableEmployees: Array<{ id: number; name: string; department: string }>;
  availableDepartments: string[];
  availableProjects: Array<{ id: string; code: string; name: string }>;
  availableCostCentres: Array<{ id: string; code: string; name: string }>;
  previewCount?: number;
  previewData?: {
    totalHours: number;
    billableHours: number;
    billablePercentage: number;
  };
}

export const AdvancedFilterDrawer: React.FC<AdvancedFilterDrawerProps> = ({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  availableEmployees,
  availableDepartments,
  availableProjects,
  availableCostCentres,
  previewCount = 0,
  previewData,
}) => {
  // Local state for working with filters before applying
  const [workingFilters, setWorkingFilters] = useState<TimesheetReportFilters>(filters);
  
  // Presets state
  const [presets, setPresets] = useState<filterPresetsApi.FilterPreset[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetsError, setPresetsError] = useState<string | null>(null);
  
  // Get data mode to determine if we should use API
  const { isLive } = useScreenDataMode('reports-timesheet');

  // Sync working filters when dialog opens or filters prop changes
  useEffect(() => {
    if (open) {
      setWorkingFilters(filters);
    }
  }, [open, filters]);
  
  // Load presets when drawer opens and in live mode
  useEffect(() => {
    if (open && isLive) {
      loadPresets();
    }
  }, [open, isLive]);
  
  // Load presets from API
  const loadPresets = useCallback(async () => {
    setPresetsLoading(true);
    setPresetsError(null);
    try {
      const loadedPresets = await filterPresetsApi.getFilterPresets();
      setPresets(loadedPresets);
    } catch (error) {
      console.error('Failed to load presets:', error);
      setPresetsError('Failed to load presets');
    } finally {
      setPresetsLoading(false);
    }
  }, []);
  
  // Handle saving a preset
  const handleSavePreset = useCallback(async (preset: Omit<FilterPreset, 'id' | 'createdAt' | 'usageCount'>) => {
    if (!isLive) {
      toast.info('Presets are only available in live mode');
      return;
    }
    
    try {
      const newPreset = await filterPresetsApi.createFilterPreset({
        name: preset.name,
        description: preset.description,
        filters: preset.filters,
        isDefault: preset.isDefault,
        isShared: preset.isShared,
        type: preset.type,
      });
      setPresets(prev => [...prev, newPreset]);
      toast.success(`Preset "${preset.name}" saved successfully`);
    } catch (error) {
      console.error('Failed to save preset:', error);
      toast.error('Failed to save preset');
    }
  }, [isLive]);
  
  // Handle deleting a preset
  const handleDeletePreset = useCallback(async (presetId: string) => {
    if (!isLive) {
      toast.info('Presets are only available in live mode');
      return;
    }
    
    try {
      await filterPresetsApi.deleteFilterPreset(presetId);
      setPresets(prev => prev.filter(p => p.id !== presetId));
      toast.success('Preset deleted successfully');
    } catch (error) {
      console.error('Failed to delete preset:', error);
      toast.error('Failed to delete preset');
    }
  }, [isLive]);
  
  // Handle setting a preset as default
  const handleSetDefault = useCallback(async (presetId: string) => {
    if (!isLive) {
      toast.info('Presets are only available in live mode');
      return;
    }
    
    try {
      await filterPresetsApi.setDefaultPreset(presetId);
      setPresets(prev => prev.map(p => ({
        ...p,
        isDefault: p.id === presetId,
      })));
      toast.success('Default preset updated');
    } catch (error) {
      console.error('Failed to set default preset:', error);
      toast.error('Failed to set default preset');
    }
  }, [isLive]);
  
  // Handle loading a preset
  const handleLoadPreset = useCallback(async (preset: FilterPreset) => {
    setWorkingFilters(preset.filters);
    
    // Increment usage count in background (don't wait for it)
    if (isLive) {
      filterPresetsApi.incrementPresetUsage(preset.id).catch(console.error);
    }
    
    toast.success(`Loaded preset: ${preset.name}`);
  }, [isLive]);

  // Count active filters in each category
  const getFilterCounts = () => {
    const counts = {
      timePeriod: 0,
      people: 0,
      projects: 0,
      status: 0,
      financial: 0,
    };

    // Time Period
    if (workingFilters.dateFrom || workingFilters.dateTo) counts.timePeriod++;

    // People & Organization
    if (workingFilters.employeeIds && workingFilters.employeeIds.length > 0) counts.people++;
    if (workingFilters.departments && workingFilters.departments.length > 0) counts.people++;

    // Projects & Cost Centres
    if (workingFilters.projectIds && workingFilters.projectIds.length > 0) counts.projects++;

    // Status
    if (workingFilters.statuses && workingFilters.statuses.length > 0) counts.status++;
    if (workingFilters.rejectedOnly) counts.status++;

    // Financial
    if (workingFilters.billableOnly) counts.financial++;

    return counts;
  };

  const filterCounts = getFilterCounts();
  const totalActiveFilters = Object.values(filterCounts).reduce((sum, count) => sum + count, 0);

  // Determine which sections should be expanded by default
  const defaultExpandedSections = useMemo(() => {
    const sections: string[] = [];
    
    // Always expand sections with active filters
    if (filterCounts.timePeriod > 0) sections.push('time-period');
    if (filterCounts.people > 0) sections.push('people');
    if (filterCounts.projects > 0) sections.push('projects');
    if (filterCounts.status > 0) sections.push('status');
    if (filterCounts.financial > 0) sections.push('financial');
    
    // If no filters are active, expand the most common ones
    if (sections.length === 0) {
      sections.push('time-period', 'people', 'status');
    }
    
    return sections;
  }, [filterCounts]);

  // Handle apply filters
  const handleApply = () => {
    onFiltersChange(workingFilters);
    onOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setWorkingFilters(filters); // Reset to original filters
    onOpenChange(false);
  };

  // Handle reset all filters
  const handleReset = () => {
    const defaultFilters: TimesheetReportFilters = {
      statuses: ['draft', 'submitted'], // Keep default status filter
    };
    setWorkingFilters(defaultFilters);
  };

  // Handle clear all filters (including defaults)
  const handleClearAll = () => {
    setWorkingFilters({});
  };

  // Detect mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "flex flex-col p-0 gap-0",
          isMobile ? "h-[90vh] rounded-t-2xl" : "w-[450px] sm:w-[450px] sm:max-w-none"
        )}
      >
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-xl flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Advanced Filters
              </SheetTitle>
              <SheetDescription className="mt-1">
                Refine your timesheet report data
              </SheetDescription>
            </div>
            {totalActiveFilters > 0 && (
              <Badge variant="secondary" className="ml-4">
                {totalActiveFilters}
              </Badge>
            )}
          </div>
        </SheetHeader>

        {/* Filter Sections - Scrollable */}
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full">
            <div className="px-6 py-4 space-y-6">
              {/* Quick Access Section - Should Have Features */}
              <div className="space-y-4">
                <Tabs defaultValue="templates" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="templates" className="gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Templates</span>
                    </TabsTrigger>
                    <TabsTrigger value="presets" className="gap-1.5">
                      <Save className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Presets</span>
                    </TabsTrigger>
                    <TabsTrigger value="history" className="gap-1.5">
                      <HistoryIcon className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">History</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="templates" className="mt-4">
                    <QuickTemplates
                      onApplyTemplate={(template: FilterTemplate) => {
                        setWorkingFilters(template.filters);
                        toast.success(`Applied template: ${template.name}`);
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="presets" className="mt-4">
                    {!isLive ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        Presets are only available in live mode. Switch to live mode to save and load filter presets.
                      </div>
                    ) : presetsLoading ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        Loading presets...
                      </div>
                    ) : presetsError ? (
                      <div className="text-center py-4 text-red-500 text-sm">
                        {presetsError}
                      </div>
                    ) : (
                      <FilterPresets
                        currentFilters={workingFilters}
                        presets={presets.map(p => ({
                          id: p.id,
                          name: p.name,
                          description: p.description,
                          filters: p.filters,
                          isDefault: p.isDefault,
                          isShared: p.isShared,
                          createdBy: p.createdBy,
                          createdAt: p.createdAt,
                          usageCount: p.usageCount,
                          type: p.type,
                        }))}
                        onLoadPreset={handleLoadPreset}
                        onSavePreset={handleSavePreset}
                        onDeletePreset={handleDeletePreset}
                        onSetDefault={handleSetDefault}
                        currentUserId={presets.length > 0 ? presets[0].createdBy : 'current-user'}
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="history" className="mt-4">
                    <FilterHistory
                      history={[]} // TODO: Connect to actual history state
                      onApplyHistory={(entry: FilterHistoryEntry) => {
                        setWorkingFilters(entry.filters);
                        toast.success('Applied filter from history');
                      }}
                      onClearHistory={() => {
                        // TODO: Implement clear history
                        console.log('Clear history');
                        toast.success('Filter history cleared');
                      }}
                    />
                  </TabsContent>
                </Tabs>
              </div>

              <Separator />

              {/* Filter Sections */}
              <Accordion 
                type="multiple" 
                defaultValue={defaultExpandedSections}
                className="space-y-2"
              >
              {/* Time Period Section */}
              <AccordionItem value="time-period" className="border rounded-lg">
                <AccordionTrigger className="hover:no-underline px-4">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">Time Period</span>
                    </div>
                    {filterCounts.timePeriod > 0 && (
                      <Badge variant="secondary" className="ml-2 h-5 px-2">
                        <Circle className="h-2 w-2 fill-blue-600 text-blue-600 mr-1" />
                        {filterCounts.timePeriod}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <TimePeriodFilters
                    filters={workingFilters}
                    onFiltersChange={setWorkingFilters}
                  />
                </AccordionContent>
              </AccordionItem>

              {/* People & Organization Section */}
              <AccordionItem value="people" className="border rounded-lg">
                <AccordionTrigger className="hover:no-underline px-4">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-green-600" />
                      <span className="font-medium">People & Organization</span>
                    </div>
                    {filterCounts.people > 0 && (
                      <Badge variant="secondary" className="ml-2 h-5 px-2">
                        <Circle className="h-2 w-2 fill-green-600 text-green-600 mr-1" />
                        {filterCounts.people}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <PeopleOrganizationFilters
                    filters={workingFilters}
                    onFiltersChange={setWorkingFilters}
                    availableEmployees={availableEmployees}
                    availableDepartments={availableDepartments}
                  />
                </AccordionContent>
              </AccordionItem>

              {/* Projects & Cost Centres Section */}
              <AccordionItem value="projects" className="border rounded-lg">
                <AccordionTrigger className="hover:no-underline px-4">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-purple-600" />
                      <span className="font-medium">Projects & Cost Centres</span>
                    </div>
                    {filterCounts.projects > 0 && (
                      <Badge variant="secondary" className="ml-2 h-5 px-2">
                        <Circle className="h-2 w-2 fill-purple-600 text-purple-600 mr-1" />
                        {filterCounts.projects}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <ProjectsFilters
                    filters={workingFilters}
                    onFiltersChange={setWorkingFilters}
                    availableProjects={availableProjects}
                    availableCostCentres={availableCostCentres}
                  />
                </AccordionContent>
              </AccordionItem>

              {/* Status & Approvals Section */}
              <AccordionItem value="status" className="border rounded-lg">
                <AccordionTrigger className="hover:no-underline px-4">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-orange-600" />
                      <span className="font-medium">Status & Approvals</span>
                    </div>
                    {filterCounts.status > 0 && (
                      <Badge variant="secondary" className="ml-2 h-5 px-2">
                        <Circle className="h-2 w-2 fill-orange-600 text-orange-600 mr-1" />
                        {filterCounts.status}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    {/* Status checkboxes */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Timesheet Status</label>
                      <div className="space-y-2">
                        {[
                          { value: 'draft' as const, label: 'Draft', icon: '📄' },
                          { value: 'submitted' as const, label: 'Submitted', icon: '🕐' },
                          { value: 'approved' as const, label: 'Approved', icon: '✓' },
                          { value: 'rejected' as const, label: 'Rejected', icon: '✗' },
                          { value: 'locked' as const, label: 'Locked', icon: '🔒' },
                        ].map((status) => (
                          <label key={status.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={workingFilters.statuses?.includes(status.value) || false}
                              onChange={(e) => {
                                const currentStatuses = workingFilters.statuses || [];
                                const newStatuses = e.target.checked
                                  ? [...currentStatuses, status.value]
                                  : currentStatuses.filter((s) => s !== status.value);
                                setWorkingFilters({ ...workingFilters, statuses: newStatuses });
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">
                              {status.icon} {status.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Additional filters */}
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={workingFilters.rejectedOnly || false}
                          onChange={(e) => {
                            setWorkingFilters({ ...workingFilters, rejectedOnly: e.target.checked });
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">With Rejections Only</span>
                      </label>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Financial & Billability Section */}
              <AccordionItem value="financial" className="border rounded-lg">
                <AccordionTrigger className="hover:no-underline px-4">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-2">
                      <PoundSterling className="h-4 w-4 text-emerald-600" />
                      <span className="font-medium">Financial & Billability</span>
                    </div>
                    {filterCounts.financial > 0 && (
                      <Badge variant="secondary" className="ml-2 h-5 px-2">
                        <Circle className="h-2 w-2 fill-emerald-600 text-emerald-600 mr-1" />
                        {filterCounts.financial}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Billability</label>
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="billability"
                            checked={!workingFilters.billableOnly}
                            onChange={() => {
                              const { billableOnly, ...rest } = workingFilters;
                              setWorkingFilters(rest);
                            }}
                            className="rounded-full border-gray-300"
                          />
                          <span className="text-sm">All</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="billability"
                            checked={workingFilters.billableOnly === true}
                            onChange={() => {
                              setWorkingFilters({ ...workingFilters, billableOnly: true });
                            }}
                            className="rounded-full border-gray-300"
                          />
                          <span className="text-sm">Billable Only</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* Results Preview */}
        {previewCount > 0 && (
          <>
            <Separator className="flex-shrink-0" />
            <div className="px-6 py-3 bg-gray-50 flex-shrink-0">
              <div className="text-sm font-medium mb-2 flex items-center gap-2">
                <Zap className="h-4 w-4 text-blue-600" />
                Results Preview
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Matching timesheets:</span>
                  <span className="font-semibold">{previewCount}</span>
                </div>
                {previewData && (
                  <>
                    <div className="flex justify-between">
                      <span>Total hours:</span>
                      <span className="font-semibold">{previewData.totalHours.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Billable hours:</span>
                      <span className="font-semibold">
                        {previewData.billableHours.toFixed(1)} ({previewData.billablePercentage.toFixed(0)}%)
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <Separator className="flex-shrink-0" />
        <SheetFooter className="px-6 py-4 flex-shrink-0 flex-row items-center justify-between sm:justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="gap-2"
              disabled={totalActiveFilters === 0}
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="gap-2 text-destructive hover:text-destructive"
              disabled={totalActiveFilters === 0}
            >
              <X className="h-4 w-4" />
              Clear All
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleApply} className="gap-2">
              <Check className="h-4 w-4" />
              Apply Filters
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
